import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle, Check, PencilSimple, X, PaperPlaneTilt, CalendarPlus, EnvelopeSimple, CheckCircle,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

function EmailAction({ action, onApprove, onDismiss }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(action.draft?.subject || "");
  const [body, setBody] = useState(action.draft?.body || "");

  const save = useMutation({
    mutationFn: () => api.editDraft(action.draft.id, { subject, body }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["actions"] }); setEditing(false); toast.success("Draft updated"); },
  });

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-4 text-xs uppercase tracking-[0.15em] font-bold text-slate-400">
        <EnvelopeSimple size={14} /> Draft reply · to {action.draft?.to}
      </div>
      {editing ? (
        <div className="space-y-3">
          <Input data-testid="edit-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-xl font-medium" />
          <Textarea data-testid="edit-body-input" value={body} onChange={(e) => setBody(e.target.value)} className="rounded-xl min-h-[180px] text-sm leading-relaxed" />
          <div className="flex gap-2">
            <Button data-testid="save-draft-btn" onClick={() => save.mutate()} disabled={save.isPending} className="rounded-xl bg-slate-900 hover:bg-slate-800">Save changes</Button>
            <Button variant="ghost" onClick={() => { setEditing(false); setSubject(action.draft.subject); setBody(action.draft.body); }} className="rounded-xl">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-slate-50/60 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border text-sm font-semibold text-slate-800">{subject}</div>
          <div className="px-4 py-3.5 text-sm text-slate-600 whitespace-pre-line leading-relaxed">{body}</div>
        </div>
      )}

      {!editing && (
        <div className="flex flex-wrap gap-2 mt-5">
          <Button data-testid={`approve-btn-${action.id}`} onClick={onApprove} className="rounded-xl bg-green-800 hover:bg-green-900 text-white gap-2">
            <PaperPlaneTilt weight="fill" size={16} /> Approve & send
          </Button>
          <Button data-testid={`edit-btn-${action.id}`} variant="outline" onClick={() => setEditing(true)} className="rounded-xl border-amber-600 text-amber-700 hover:bg-amber-50 gap-2">
            <PencilSimple weight="bold" size={16} /> Edit
          </Button>
          <Button data-testid={`dismiss-btn-${action.id}`} variant="ghost" onClick={onDismiss} className="rounded-xl text-rose-700 hover:bg-rose-50 gap-2">
            <X weight="bold" size={16} /> Dismiss
          </Button>
        </div>
      )}
    </div>
  );
}

function ScheduleAction({ action, onDismiss, invalidate }) {
  const [sel, setSel] = useState(0);
  const pe = action.proposed_event || {};
  const options = pe.options || [];

  const approve = useMutation({
    mutationFn: () => api.approve(action.id, sel),
    onSuccess: () => { invalidate(); toast.success("Event created & invite sent"); },
    onError: () => toast.error("Could not create event"),
  });

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-[0.15em] font-bold text-slate-400">
        <CalendarPlus size={14} /> Proposed meeting · {pe.duration_minutes || 30} min
      </div>
      <div className="font-heading font-semibold text-slate-900 mb-1">{pe.title}</div>
      {pe.reasoning && <p className="text-sm text-slate-500 mb-4">{pe.reasoning}</p>}
      <div className="space-y-2 mb-5">
        {options.map((o, i) => (
          <button
            key={i}
            data-testid={`time-option-${action.id}-${i}`}
            onClick={() => setSel(i)}
            className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
              sel === i ? "border-green-700 bg-green-50" : "border-border hover:bg-slate-50"
            }`}
          >
            <span className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${sel === i ? "border-green-700 bg-green-700" : "border-slate-300"}`}>
              {sel === i && <Check weight="bold" size={10} className="text-white" />}
            </span>
            <span className="text-sm font-medium text-slate-800">{o.label}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid={`approve-btn-${action.id}`} onClick={() => approve.mutate()} disabled={approve.isPending} className="rounded-xl bg-green-800 hover:bg-green-900 text-white gap-2">
          <Check weight="bold" size={16} /> {approve.isPending ? "Creating…" : "Approve & schedule"}
        </Button>
        <Button data-testid={`dismiss-btn-${action.id}`} variant="ghost" onClick={onDismiss} className="rounded-xl text-rose-700 hover:bg-rose-50 gap-2">
          <X weight="bold" size={16} /> Dismiss
        </Button>
      </div>
    </div>
  );
}

export default function PendingActions() {
  const qc = useQueryClient();
  const { data: actions, isLoading } = useQuery({ queryKey: ["actions"], queryFn: api.actions });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["actions"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["calendar"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
  };

  const approve = useMutation({
    mutationFn: (id) => api.approve(id, 0),
    onSuccess: () => { invalidate(); toast.success("Reply approved & sent"); },
    onError: () => toast.error("Could not approve"),
  });
  const dismiss = useMutation({
    mutationFn: (id) => api.dismiss(id),
    onSuccess: () => { invalidate(); toast("Action dismissed"); },
  });

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
      <PageHeader
        testid="actions-header"
        eyebrow="Trust-first · nothing sent without you"
        title="Pending Actions"
        subtitle="Review what Auppo prepared. Approve, edit, or dismiss."
      />

      {isLoading ? (
        <div className="text-center py-16 text-slate-400 aria-thinking">Loading…</div>
      ) : (actions?.length || 0) === 0 ? (
        <div className="text-center py-20" data-testid="actions-empty">
          <div className="h-14 w-14 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle weight="fill" className="text-green-700" size={30} />
          </div>
          <h3 className="font-heading text-xl font-medium text-slate-800">You're all caught up</h3>
          <p className="text-slate-500 mt-1">No actions awaiting approval. Draft a reply from your Inbox to see it here.</p>
        </div>
      ) : (
        <div className="space-y-5">
          <AnimatePresence mode="popLayout">
            {actions.map((a) => (
              <motion.div
                key={a.id}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.25 }}
                data-testid={`action-card-${a.id}`}
                className="bg-white border border-border rounded-2xl overflow-hidden"
              >
                <div className="px-6 pt-5 pb-0 flex items-start gap-3">
                  <div className="h-8 w-8 shrink-0 rounded-lg bg-slate-900 flex items-center justify-center">
                    <Sparkle weight="fill" size={15} className="text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-900 leading-snug">{a.title}</div>
                    <div className="text-sm text-slate-500 mt-0.5">{a.context}</div>
                  </div>
                </div>
                {a.type === "email_reply" ? (
                  <EmailAction action={a} onApprove={() => approve.mutate(a.id)} onDismiss={() => dismiss.mutate(a.id)} />
                ) : (
                  <ScheduleAction action={a} invalidate={invalidate} onDismiss={() => dismiss.mutate(a.id)} />
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
